import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { createClaim, updateClaim, getClaim, deleteClaimByUserIdAndCaseId, submitValuation, deleteClaimPhotosBySection, deleteAllClaimPhotos, retrainAI, saveAiResults } from '@/services/claimService'
import type { RetrainField, RetrainPayload, RetrainSection } from '@/services/claimService'
import { useValuationStore } from '@/store/valuationStore'
import { useUiStore } from '@/store/uiStore'
import { useAuthStore } from '@/store/authStore'
import { clearAllDraftData } from '@/services/idbService'
import { formValuesToClaimPayload, claimResponseToFormValues } from '@/utils/claimPayloadMapper'
import { useRefStore } from '@/store/refStore'
import { useAttributeStore } from '@/store/attributeStore'
import { preloadRefCategories } from '@/utils/preloadRefCategories'
import { parseApiErrors } from '@/utils/apiErrorMapper'
import { getMessage } from '@/utils/messages'
import { handleApiError } from '@/utils/handleApiError'
import { log } from '@/utils/logger'
import type { FieldMetadata, FormValues, MappedField, PhotoItem } from '@/types'

// ── Retrain payload builder ───────────────────────────────────────────────────
// Compares current form values against what AI originally wrote (fieldMetadata.aiValue)
// and groups changed fields by their source photo section for the retrain API.
function buildRetrainPayload(
  claimId: string,
  caseId: string,
  userId: number,
  formValues: FormValues,
  fieldMetadata: Record<string, Record<string, FieldMetadata>>,
  aiResults: Record<string, MappedField[]>,
  ocrResults: Record<string, MappedField[]>,
  photos: PhotoItem[]
): RetrainPayload | null {
  const groups = new Map<string, { sectionId: string; imageName: string; fields: RetrainField[] }>()

  const collectChanges = (
    photoSectionId: string,
    mappedFields: MappedField[],
    groupKey: string,
    isOcr: boolean
  ) => {
    for (const mf of mappedFields) {
      const meta = fieldMetadata[mf.sectionId]?.[mf.fieldKey]
      if (!meta?.aiValue) continue
      const currentVal = formValues[mf.sectionId]?.[mf.fieldKey] ?? ''
      if (currentVal === meta.aiValue) continue

      if (!groups.has(groupKey)) {
        const photo = photos.find(
          (p) =>
            p.sectionId === photoSectionId &&
            (isOcr
              ? p.blobSection === 'ocr' || p.blobSection === 'form-recognizer'
              : p.blobSection !== 'ocr' && p.blobSection !== 'form-recognizer') &&
            p.serverName
        )
        groups.set(groupKey, { sectionId: photoSectionId, imageName: photo?.serverName ?? photoSectionId, fields: [] })
      }
      groups.get(groupKey)!.fields.push({ FieldName: mf.fieldKey, OldValue: meta.aiValue, NewValue: currentVal, BoxValue: mf.boxValue ?? null })
    }
  }

  for (const [photoSectionId, fields] of Object.entries(aiResults)) {
    collectChanges(photoSectionId, fields, `ai:${photoSectionId}`, false)
  }
  for (const [sectionId, fields] of Object.entries(ocrResults)) {
    collectChanges(sectionId, fields, `ocr:${sectionId}`, true)
  }

  if (groups.size === 0) return null

  const sections: RetrainSection[] = []
  for (const { sectionId, imageName, fields } of groups.values()) {
    sections.push({ SectionCode: sectionId, ImageName: imageName, Fields: fields })
  }

  return { ClaimId: claimId, CaseId: caseId, UserId: userId.toString(), Sections: sections }
}

/**
 * Mutation hook for saving a claim draft.
 *
 * Flow:
 *  1. Map current formValues → structured ClaimDraftPayload
 *  2. POST (new) or PUT (existing) to /api/claim/draft or /api/claim/:id
 *  3. Call GET /api/claim/:id to retrieve canonical state from server
 *  4. Map GET response → FormValues → save to IndexedDB + update Zustand state
 */
export function useSaveDraft() {
  const { showToast, setApiFieldErrors, clearApiFieldErrors } = useUiStore()

  return useMutation({
    mutationFn: async (_options?: { silent?: boolean }) => {
      // Read fresh state inside mutationFn to avoid stale closure on draftId/formValues
      const { draftId: currentDraftId, formValues: currentFormValues, photos: currentPhotos, pendingDeletions } = useValuationStore.getState()

      // Flush deferred photo deletions accumulated from field-change resets / user removals.
      // Use the widest applicable endpoint: all-photos > section > individual.
      const caseIdForDeletion = useValuationStore.getState().caseId ?? ''
      const userIdForDeletion = String(useAuthStore.getState().user?.userId ?? '')
      if (userIdForDeletion && caseIdForDeletion) {
        if (pendingDeletions.allPhotos) {
          await deleteAllClaimPhotos(userIdForDeletion, caseIdForDeletion).catch((e) => log.error('deleteAllClaimPhotos failed', e instanceof Error ? e : new Error(String(e))))
        } else {
          const ops: Promise<unknown>[] = []
          if (pendingDeletions.sections.length > 0) {
            ops.push(deleteClaimPhotosBySection(userIdForDeletion, caseIdForDeletion, pendingDeletions.sections))
          }
          // sections stores blob-prefixed paths (e.g. 'ai/standard-equipment'); extract raw sectionId for the coverage check
          if (ops.length > 0) await Promise.allSettled(ops)
        }
      }

      // Snapshot the form values at send-time so onSuccess can smart-merge and avoid
      // overwriting fields the user changes while this request is in-flight.
      const sentFormValues = currentFormValues
      const getRefId = useRefStore.getState().getId
      const attrCache = useAttributeStore.getState().cache
      const payload = formValuesToClaimPayload(currentFormValues, getRefId, currentPhotos, attrCache)

      if (!currentDraftId) {
        const userId = useAuthStore.getState().user?.userId
        if (userId) payload.userId = userId.toString()
      }

      const storeCaseId = useValuationStore.getState().caseId
      if (storeCaseId) payload.caseId = storeCaseId

      // POST to create if no draftId, PUT to update if draftId exists
      const saveRes = currentDraftId
        ? await updateClaim(currentDraftId, payload)
        : await createClaim(payload)

      if (!saveRes.success) {
        if (saveRes.errors?.length) {
          setApiFieldErrors(parseApiErrors(saveRes.errors))
        }
        throw new Error(saveRes.message ?? getMessage('valuation.error.save_failed'))
      }

      const claimId = String(saveRes.data?.id ?? currentDraftId ?? '')

      // Set draftId immediately after save succeeds — before anything else that might throw
      // This ensures subsequent calls use updateClaim even if getClaim below fails
      if (claimId) {
        useValuationStore.getState().setDraftId(claimId)
      }

      if (!claimId) throw new Error(getMessage('api.error.unexpected'))

      // Fetch canonical claim data from server
      const getRes = await getClaim(claimId)
      // Ensure body-information ref option data (incl. shared ref_category
      // fields) is loaded into refStore before the dropdown values are mapped
      // in onSuccess — otherwise the resolved label may not yet be present in
      // the field's rendered <select> options.
      if (getRes.data) await preloadRefCategories(getRes.data)
      return { claimId, claimData: getRes.data, sentFormValues }
    },

    onSuccess: ({ claimId, claimData, sentFormValues }, variables) => {
      log.info('saveDraft success', { claimId, silent: String(variables?.silent ?? false) })
      // Map GET response → FormValues, save to IDB and update Zustand.
      // Pass sentFormValues as the baseline so setClaimData only applies server
      // values for fields that haven't changed since this mutation was sent.
      const getRefLabel = useRefStore.getState().getLabelById
      const populatedFormValues = claimResponseToFormValues(claimData, getRefLabel, useAttributeStore.getState().cache)
      useValuationStore.getState().setClaimData(claimId, populatedFormValues, sentFormValues)
      if (claimData?.caseId && !useValuationStore.getState().caseId) {
        useValuationStore.getState().setCaseId(claimData.caseId)
      }
      useValuationStore.getState().clearPendingDeletions()
      useValuationStore.getState().setIsDirty(false)
      clearApiFieldErrors()
      if (!variables?.silent) showToast(getMessage('valuation.success.draft_saved'))

      // Persist AI/OCR metadata to backend — non-blocking, non-fatal
      const caseId = useValuationStore.getState().caseId ?? ''
      const userId = String(useAuthStore.getState().user?.userId ?? '')
      if (userId && caseId) {
        const { photos, fieldMetadata, aiResults, aiRawPayloads, ocrResults, photoResults } =
          useValuationStore.getState()
        // Convert photoResults from photoId-keyed (ephemeral) to serverName-keyed (stable)
        const serverNamePhotoResults: Record<string, MappedField[]> = {}
        for (const [photoId, results] of Object.entries(photoResults)) {
          const photo = photos.find((p) => p.id === photoId)
          if (photo?.serverName) serverNamePhotoResults[photo.serverName] = results
        }
        saveAiResults(userId, caseId, {
          fieldMetadata,
          aiResults,
          aiRawPayloads,
          ocrResults,
          photoResults: serverNamePhotoResults,
        }).catch((e) => log.warn('saveDraft AI/OCR metadata save failed (non-fatal)', { caseId, error: String(e) }))
      }
    },

    onError: (err: unknown) => {
      log.error('saveDraft failed', err instanceof Error ? err : new Error(String(err)))
      handleApiError(err)
    },
  })
}

/**
 * Mutation hook for final valuation submission.
 * On success, clears all IDB draft data so the app is ready for a new inspection.
 */
export function useSubmitValuation() {
  const navigate = useNavigate()
  const { draftId, setStatus } = useValuationStore()
  const { showToast } = useUiStore()

  return useMutation({
    mutationFn: () => {
      if (!draftId) throw new Error(getMessage('valuation.error.no_draft_id'))
      return submitValuation(draftId)
    },
    onSuccess: () => {
      // Fire retrain non-blocking — detects fields where the user changed an AI-written value.
      const { formValues, fieldMetadata, aiResults, ocrResults, photos, caseId } = useValuationStore.getState()
      const claimRefNo = (formValues['basic-information']?.['claimRefNo'] ?? '').trim()
      const userId = useAuthStore.getState().user?.userId ?? 0
      const retrainPayload = buildRetrainPayload(
        claimRefNo || draftId!,
        caseId ?? '',
        userId,
        formValues,
        fieldMetadata,
        aiResults,
        ocrResults,
        photos
      )
      if (retrainPayload) {
        retrainAI(retrainPayload).catch((err) => log.error('retrainAI call failed (non-fatal)', err instanceof Error ? err : new Error(String(err)), { claimId: draftId ?? '' }))
      }

      clearAllDraftData().catch((e) => log.error('IDB clearAllDraftData failed', e instanceof Error ? e : new Error(String(e))))
      log.info('submitValuation success', { claimId: draftId ?? '' })
      setStatus('submitted')
      useValuationStore.getState().setIsDirty(false)
      showToast(getMessage('valuation.success.submitted'))
      setTimeout(() => navigate('/dashboard'), 1500)
    },
    onError: (err: unknown) => {
      log.error('submitValuation failed', err instanceof Error ? err : new Error(String(err)), { claimId: draftId ?? '' })
      handleApiError(err)
    },
  })
}

export function useDeleteClaim() {
  return useMutation({
    mutationFn: ({ userId, caseId }: { userId: string; caseId: string }) => {
      log.info('deleteClaim', { userId, caseId })
      return deleteClaimByUserIdAndCaseId(userId, caseId)
    },
    onSuccess: (_data, { userId, caseId }) => {
      log.info('deleteClaim success', { userId, caseId })
    },
    onError: (err: unknown) => {
      log.error('deleteClaim failed', err instanceof Error ? err : new Error(String(err)))
      handleApiError(err)
    },
  })
}
